import gsap from "gsap"
import { color, uniform } from "three/tsl"
import * as THREE from "three/webgpu"

import { MSDFTextGeometry, MSDFTextNodeMaterial } from "../"

export type MSDFFont = {
  data: object
}

export default class TextFollowingMouse {
  scene: THREE.Scene
  textMesh!: THREE.Mesh<MSDFTextGeometry, MSDFTextNodeMaterial>
  textGeometry!: MSDFTextGeometry
  textMaterial!: MSDFTextNodeMaterial
  text: string
  progressUniform!: ReturnType<typeof uniform>

  private scrambleTween?: gsap.core.Tween
  private fadeTween?: gsap.core.Tween
  private noiseTexture?: THREE.Texture

  private updateCounter: number = 0
  private lastScrambledText: string = ""
  private onClickCallback?: () => void
  private clickHandler?: (event: MouseEvent) => void

  constructor({
    scene,
    atlas,
    font,
    text,
    startHidden = false,
    onClick,
    noiseTexture,
  }: {
    scene: THREE.Scene
    atlas: THREE.Texture
    font: MSDFFont
    text: string
    startHidden?: boolean
    onClick?: () => void
    noiseTexture?: THREE.Texture
  }) {
    this.scene = scene

    this.text = text
    this.onClickCallback = onClick

    this.noiseTexture = noiseTexture

    this.createText(atlas, font, startHidden)
  }

  private setupClickHandler() {
    if (!this.onClickCallback || this.clickHandler) {
      return
    }

    this.clickHandler = () => {
      this.onClickCallback?.()
    }

    document.body.addEventListener("click", this.clickHandler)
  }

  private removeClickHandler() {
    if (this.clickHandler) {
      document.body.removeEventListener("click", this.clickHandler)
      this.clickHandler = undefined
    }
  }

  destroy() {
    this.killTweens()
    this.removeClickHandler()

    if (this.textMesh) {
      this.scene.remove(this.textMesh)
      this.textGeometry.dispose()
      this.textMaterial.dispose()
    }
  }

  createText(atlas: THREE.Texture, font: MSDFFont, startHidden: boolean) {
    const geometry = new MSDFTextGeometry({
      text: startHidden ? "" : this.text,
      font: font.data,
    })

    const progressUniform = uniform(startHidden ? 0 : 1)

    const material = new MSDFTextNodeMaterial({
      isSmooth: 1,
      strokeOutsetWidth: 0,
      strokeInsetWidth: 0,
      threshold: 0.5,
      map: atlas,
      fade: uniform(false),
      progress: progressUniform,
      color: color("#acacac"),
      noiseTexture: this.noiseTexture,
    })
    material.side = THREE.DoubleSide

    this.progressUniform = progressUniform
    this.textGeometry = geometry
    this.textMaterial = material

    this.textMesh = new THREE.Mesh(geometry, material)
    this.textMesh.rotateX(Math.PI)
    this.textMesh.visible = !startHidden

    this.scene.add(this.textMesh)
  }

  private killTweens() {
    this.scrambleTween?.kill()
    this.fadeTween?.kill()
    this.scrambleTween = undefined
    this.fadeTween = undefined
  }

  private textScramble(text: string, progress: number) {
    // Split by spaces and newlines, preserving delimiters
    const tokens = text.split(/(\s+|\n+)/)
    const wordCount = Math.ceil(tokens.length / 2)
    const visibleWorlds = wordCount - Math.ceil(wordCount * (1 - progress))

    const chars = "jompaWB)#@$"
    const charsLength = chars.length
    let wordIndex = 0
    let scrubled = ""

    // Single pass: process tokens directly
    for (let i = 0; i < tokens.length; i++) {
      if (i % 2 === 0) {
        const world = tokens[i] // words
        const delimiter = tokens[i + 1] || ""

        if (wordIndex <= visibleWorlds) {
          scrubled += world + delimiter
        } else {
          // Random word
          const wordLength = world.length
          let randomWord = ""
          for (let j = 0; j < wordLength; j++) {
            randomWord += chars[Math.floor(Math.random() * charsLength)]
          }
          scrubled += randomWord + delimiter
        }
        wordIndex++
      }
    }

    return scrubled
  }

  reveal({
    duration = 0.5,
  }: {
    duration?: number
  } = {}) {
    if (!this.text) {
      return
    }

    this.killTweens()
    this.textMesh.visible = true

    const proxy = { progress: 0 }

    this.updateCounter = 0
    this.lastScrambledText = ""

    this.scrambleTween = gsap.to(proxy, {
      progress: 1,
      duration,
      ease: "none",
      onUpdate: () => {
        this.updateCounter++
        // Only call textScramble every 5 onUpdate calls (or on first call)
        if (this.updateCounter % 5 === 0 || this.lastScrambledText === "") {
          this.lastScrambledText = this.textScramble(this.text, proxy.progress)
        }

        this.textGeometry.update({
          text: this.lastScrambledText,
        })
      },
      onComplete: () => {
        this.textGeometry.update({
          text: this.text,
        })
      },
    })

    const fadeProxy = { progress: 0 }

    this.fadeTween = gsap.to(fadeProxy, {
      progress: 1,
      duration,
      ease: "power2.out",
      onUpdate: () => {
        this.progressUniform.value = fadeProxy.progress
      },
      onComplete: () => {
        // Set up click handler only after text is fully revealed
        this.setupClickHandler()
      },
    })
  }

  leave({
    duration = 0.5,
  }: {
    duration?: number
  } = {}) {
    this.killTweens()
    // Remove click handler immediately when leaving starts
    this.removeClickHandler()

    const startFade =
      typeof this.progressUniform?.value === "number"
        ? this.progressUniform.value
        : 1
    const fadeProxy = { progress: startFade }

    // Fade always runs; when it completes we hide + clear geometry.
    this.fadeTween = gsap.to(fadeProxy, {
      progress: 0,
      duration,
      ease: "power2.in",
      onUpdate: () => {
        this.progressUniform.value = fadeProxy.progress
      },
      onComplete: () => {
        this.textGeometry.update({
          text: "",
        })
        this.textMesh.visible = false
      },
    })

    if (!this.text) {
      return
    }

    const proxy = { progress: 1 }

    this.updateCounter = 0
    this.lastScrambledText = this.text

    this.scrambleTween = gsap.to(proxy, {
      progress: 0,
      duration,
      ease: "none",
      onUpdate: () => {
        this.updateCounter++
        // Only call textScramble every 5 onUpdate calls (or on first call)
        if (
          this.updateCounter % 5 === 0 ||
          this.lastScrambledText === this.text
        ) {
          this.lastScrambledText = this.textScramble(this.text, proxy.progress)
        }

        this.textGeometry.update({
          text: this.lastScrambledText,
        })
      },
    })
  }

  updateText(text: string) {
    this.text = text
    this.updateCounter = 0
    this.lastScrambledText = ""

    // If currently visible, update immediately; otherwise keep it cleared until reveal().
    if (this.textMesh?.visible) {
      this.textGeometry.update({
        text: this.text,
      })
    }
  }

  update() {
    // Intentionally left blank: external code can drive position each frame.
    // Text updates are handled by updateText() / reveal() / leave().
  }
}
